import { useMemo } from "react";
import { useAuth } from "../contexts/auth";
import { getUserHighestPermission, permissionTypes } from "../utils/getUserHighestPermission";

export function usePermission() {
    const { adminUser } = useAuth();

    const highestPermission = useMemo(() => {
        if (!adminUser) return undefined;
        return getUserHighestPermission(adminUser.permissions);
    }, [adminUser]);

    const isAboveCommonUser = useMemo(() => {
        if (!highestPermission) return undefined;
        return highestPermission?.level > permissionTypes.indexOf("Usuario");
    }, [highestPermission]);

    const isAboveAttendant = useMemo(() => {
        if (!highestPermission) return undefined;
        return highestPermission?.level > permissionTypes.indexOf("Atendente");
    }, [highestPermission]);

    const isAboveCoordenator = useMemo(() => {
        if (!highestPermission) return undefined;
        return highestPermission?.level > permissionTypes.indexOf("Coordenador de Atendimento");
    }, [highestPermission]);

    const isAboveManager = useMemo(() => {
        if (!highestPermission) return undefined;
        return highestPermission?.level > permissionTypes.indexOf("Gerente de Atendimento");
    }, [highestPermission]);

    const isAboveAdmin = useMemo(() => {
        if (!highestPermission) return undefined;
        const adminLevel = Math.max(
            permissionTypes.indexOf("Administrador"),
            permissionTypes.indexOf("Administrador de Atendimento")
        );
        return highestPermission?.level > adminLevel;
    }, [highestPermission]);

    const hasCommonUserProfile = useMemo(() => {
        return adminUser?.permissions?.includes("Usuario");
    }, [adminUser]);

    const hasAttendantProfile = useMemo(() => {
        return adminUser?.permissions?.includes("Atendente");
    }, [adminUser]);

    const hasCoordenatorProfile = useMemo(() => {
        return adminUser?.permissions?.includes("Coordenador") || 
               adminUser?.permissions?.includes("Coordenador de Atendimento");
    }, [adminUser]);

    const hasManagerProfile = useMemo(() => {
        return adminUser?.permissions?.includes("Gerente") || 
               adminUser?.permissions?.includes("Gerente de Atendimento");
    }, [adminUser]);

    const hasAdminProfile = useMemo(() => {
        return adminUser?.permissions?.includes("Administrador") || 
               adminUser?.permissions?.includes("Administrador de Atendimento");
    }, [adminUser]);

    const hasDeveloperProfile = useMemo(() => {
        return adminUser?.permissions?.includes("Desenvolvedor");
    }, [adminUser]);

    function canAccess(allowedRoles?: string[]) {
        if (!allowedRoles || allowedRoles.length === 0) {
            return true;
        }

        return adminUser?.permissions?.some(
            permission => allowedRoles.includes(permission)
        );
    }

    return {
        highestPermission,
        isAboveCommonUser,
        isAboveAttendant,
        isAboveCoordenator,
        isAboveManager,
        isAboveAdmin,
        hasCommonUserProfile,
        hasAttendantProfile,
        hasCoordenatorProfile,
        hasManagerProfile,
        hasAdminProfile,
        hasDeveloperProfile,
        canAccess
    };
}
